import { useEffect, useRef } from 'react';
import { Link } from 'react-router';
import { toast } from 'sonner';
import { Button } from '@/components/atoms/button';
import { useImportJobStream } from '@/features/zones/hooks/useImportJobStream';
import { useImportJobOutcomeQuery } from '@/features/zones/services/zones.api';
import { ZONES_MIXPANEL_EVENTS } from '@/features/zones/utils/mixpanelEvents';
import { useMixpanel } from '@/hooks/useMixpanel';
import { notifyError } from '@/libs/bugsnag';
import type { ImportJobStatus, ISyncCounts, ISyncOutcomeRow, SyncOutcomeReason } from '@/types/api';

const STATUS_LABELS: Record<ImportJobStatus, string> = {
  queued: 'Queued — waiting for a worker…',
  syncing: 'Syncing from Notion…',
  persisting: 'Saving zones…',
  completed: 'Sync complete',
  failed: 'Sync failed',
};

const REASON_LABELS: Record<SyncOutcomeReason, string> = {
  name_missing: 'Name missing in Notion',
  geometry_missing: 'Geometry missing in Notion',
  geometry_invalid_json: 'Geometry is not valid JSON',
  geometry_not_polygon: 'Geometry is not a polygon',
  geometry_invalid: 'Geometry is self-intersecting or degenerate',
  invalid_zone_payload: 'Zone payload rejected at commit',
};

function summaryLine(c: ISyncCounts): string {
  return `${c.created} created · ${c.updated} updated · ${c.skipped} skipped · ${c.failed} failed · ${c.deleted} deleted`;
}

function CountsBar({ counts }: { counts: ISyncCounts }) {
  return (
    <dl className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-5">
      {(
        [
          ['Created', counts.created],
          ['Updated', counts.updated],
          ['Skipped', counts.skipped],
          ['Failed', counts.failed],
          ['Deleted', counts.deleted],
        ] as const
      ).map(([label, value]) => (
        <div key={label} className="rounded-lg border border-primary-100 p-3 text-center">
          <dt className="text-xs uppercase tracking-wide text-muted-foreground">{label}</dt>
          <dd className="mt-1 text-2xl font-semibold tabular-nums">{value}</dd>
        </div>
      ))}
    </dl>
  );
}

function OutcomeRow({ row }: { row: ISyncOutcomeRow }) {
  return (
    <tr className="border-b border-primary-50">
      <td className="px-3 py-2 font-medium">{row.decision}</td>
      <td className="px-3 py-2 text-muted-foreground">
        {row.reason ? REASON_LABELS[row.reason] : '—'}
      </td>
      <td className="px-3 py-2 font-mono text-xs">{row.notion_page_id}</td>
    </tr>
  );
}

/**
 * Post-sync outcome screen. While the job is in
 * progress it renders live SSE progress (`useImportJobStream`); once the
 * job is terminal it fetches and renders the per-row outcome, fires a
 * one-shot summary toast (counts by outcome), and offers a "View zones"
 * CTA. An empty result shows a friendly empty state; a failed sync shows a
 * failure state.
 */
export function SyncOutcomeScreen({ jobId }: { jobId: string }) {
  const stream = useImportJobStream(jobId);
  const {
    data: outcome,
    isError: outcomeError,
    error: outcomeErrorObj,
    refetch: refetchOutcome,
  } = useImportJobOutcomeQuery(jobId, stream.isTerminal);
  const trackAction = useMixpanel((s) => s.trackAction);
  const summarisedRef = useRef(false);

  // Report unexpected infra failures to Bugsnag. These are
  // not user-actionable: the SSE stream died AND its JSON snapshot fallback
  // failed (`stream.error`), or the terminal outcome fetch failed
  // (`outcomeError`). A sync that merely reaches `failed` is a product
  // outcome (tracked via Mixpanel), not a monitored error.
  const streamError = stream.error;
  useEffect(() => {
    if (streamError) {
      notifyError(streamError, { context: 'zones:sync:stream', metadata: { job_id: jobId } });
    }
  }, [streamError, jobId]);

  useEffect(() => {
    if (outcomeError) {
      notifyError(outcomeErrorObj ?? new Error('Notion sync outcome fetch failed'), {
        context: 'zones:sync:outcome',
        metadata: { job_id: jobId },
      });
    }
    // `outcomeError` (isError) is the stable terminal-failure signal. We
    // deliberately omit `outcomeErrorObj` from the deps: a failed Retry
    // yields a new Error reference, which would otherwise re-fire this
    // effect and double-report the same logical failure.
  }, [outcomeError, jobId]);

  const viewZonesCta = (
    <Button asChild>
      <Link to="/zones">View zones</Link>
    </Button>
  );

  useEffect(() => {
    if (!outcome || summarisedRef.current) return;
    summarisedRef.current = true;
    if (outcome.status === 'failed') {
      trackAction(ZONES_MIXPANEL_EVENTS.SYNC_FAILED, { job_id: jobId });
      return;
    }
    toast.success(`Sync complete — ${summaryLine(outcome.counts)}.`);
    trackAction(ZONES_MIXPANEL_EVENTS.SYNC_COMPLETED, { job_id: jobId, ...outcome.counts });
  }, [outcome, jobId, trackAction]);

  // ── In progress: live SSE ────────────────────────────────────────────────
  if (!stream.isTerminal) {
    const status = stream.status ?? 'queued';
    return (
      <div>
        <h1 className="text-2xl font-semibold">Sync in progress</h1>
        <p className="mt-2 text-muted-foreground" aria-live="polite">
          {STATUS_LABELS[status]}
        </p>
        {stream.counts ? <CountsBar counts={stream.counts} /> : null}
      </div>
    );
  }

  // ── Terminal: failed ─────────────────────────────────────────────────────
  if (stream.status === 'failed' || outcome?.status === 'failed') {
    return (
      <div>
        <h1 className="text-2xl font-semibold text-destructive">Sync failed</h1>
        <p className="mt-2 text-muted-foreground">
          The sync could not complete. Check the Notion connection and try again.
        </p>
        {outcome ? <CountsBar counts={outcome.counts} /> : null}
        <div className="mt-6">
          <Button asChild variant="outline">
            <Link to="/zones">Back to zones</Link>
          </Button>
        </div>
      </div>
    );
  }

  // Terminal completed but the outcome fetch errored — offer a way out
  // instead of stranding the user on a perpetual loading message.
  if (outcomeError) {
    return (
      <div>
        <h1 className="text-2xl font-semibold">Sync complete</h1>
        <p className="mt-2 text-muted-foreground">
          The sync finished, but the per-row outcome couldn't be loaded.
        </p>
        <div className="mt-6 flex gap-2">
          <Button variant="outline" onClick={() => refetchOutcome()}>
            Retry
          </Button>
          {viewZonesCta}
        </div>
      </div>
    );
  }

  // Terminal completed but the outcome fetch hasn't resolved yet.
  if (!outcome) {
    return <p className="text-muted-foreground">Loading sync outcome…</p>;
  }

  // ── Terminal: completed, no rows ─────────────────────────────────────────
  if (outcome.outcome_rows.length === 0) {
    return (
      <div>
        <h1 className="text-2xl font-semibold">Sync complete</h1>
        <p className="mt-2 text-muted-foreground">No zones to sync from Notion.</p>
        <div className="mt-6">{viewZonesCta}</div>
      </div>
    );
  }

  // ── Terminal: completed, with rows ───────────────────────────────────────
  return (
    <div>
      <h1 className="text-2xl font-semibold">Sync complete</h1>
      <CountsBar counts={outcome.counts} />
      <table className="mt-6 w-full border-collapse text-left text-sm">
        <thead>
          <tr className="border-b border-primary-100 text-xs uppercase tracking-wide text-muted-foreground">
            <th scope="col" className="px-3 py-2 font-medium">
              Outcome
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              Reason
            </th>
            <th scope="col" className="px-3 py-2 font-medium">
              Notion page
            </th>
          </tr>
        </thead>
        <tbody>
          {outcome.outcome_rows.map((row) => (
            <OutcomeRow key={row.notion_page_id} row={row} />
          ))}
        </tbody>
      </table>
      <div className="mt-6">{viewZonesCta}</div>
    </div>
  );
}
